import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberLevelDto, MemberLevelGrantsDto, MemberLevelValidity } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { useOperationKey } from './ledger/walletShared';

export const taipeiLevelTime = (value: string) =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(value));

export function MemberLevelGrants({
  memberId,
  levels,
}: {
  memberId: string;
  levels: MemberLevelDto[];
}) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const canUpdate = useAuthStore((s) => !!s.allowedActions?.includes('member:update'));
  const [levelId, setLevelId] = useState('');
  const [validity, setValidity] = useState<MemberLevelValidity>('WEEK');
  const [reason, setReason] = useState('');
  const [endId, setEndId] = useState('');
  // The change form stays closed until staff ask for it; ending a grant opens it directly.
  const [formOpen, setFormOpen] = useState(false);
  const formId = useId();
  const query = useQuery({
    queryKey: ['member-level-grants', memberId],
    queryFn: () => api.get<MemberLevelGrantsDto>(`/members/${memberId}/level-grants`),
  });
  const key = useOperationKey(
    { memberId, levelId, validity, reason: reason.trim(), endId },
    async () => {
      await query.refetch({ throwOnError: true });
    },
  );
  const mutation = useMutation({
    mutationFn: () =>
      endId
        ? api.post(`/members/${memberId}/level-grants/${endId}/end`, {
            reason,
            operationKey: key.key,
          })
        : api.post(`/members/${memberId}/level-grants`, {
            levelId,
            validity,
            reason,
            operationKey: key.key,
          }),
    onSuccess: () => {
      key.renew();
      setReason('');
      setEndId('');
      setFormOpen(false);
      qc.invalidateQueries({ queryKey: ['member-level-grants', memberId] });
      qc.invalidateQueries({ queryKey: ['member', memberId] });
      qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
    },
    onError: key.failed,
  });
  const name = (id: string | null) => levels.find((l) => l.id === id)?.displayName ?? id ?? '-';
  const field =
    'block w-full min-h-11 px-3 rounded-lg border border-base-300 bg-base-300 text-base-content text-base focus:outline-hidden focus:ring-2 focus:ring-primary disabled:opacity-50';
  const labelClass = 'mb-2 block text-sm font-medium text-base-content/80';
  const button =
    'inline-flex min-h-11 items-center px-4 rounded-lg border border-base-300 text-sm hover:bg-base-300 disabled:opacity-50';
  const showForm = canUpdate && (formOpen || !!endId);
  const closeForm = () => {
    setFormOpen(false);
    setEndId('');
    setReason('');
  };
  return (
    <section className="space-y-2 rounded-lg border border-base-300 p-3 my-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{t('levelGrants')}</h2>
        {canUpdate && (
          <button
            type="button"
            className={button}
            aria-expanded={showForm}
            aria-controls={formId}
            disabled={mutation.isPending}
            onClick={() => (showForm ? closeForm() : setFormOpen(true))}
          >
            {t('levelChange')}
          </button>
        )}
      </div>
      {query.isError && (
        <p role="alert" className="text-error">
          {String(query.error.message)}
        </p>
      )}
      {query.data?.grants.length === 0 && (
        <p className="text-sm text-base-content/70">{t('levelGrantsEmpty')}</p>
      )}
      {query.data?.grants.map((grant) => (
        <div key={grant.id} className="border-b border-base-300 pb-2 text-sm">
          <p>
            {grant.level.displayName} ·{' '}
            {grant.endsAt ? taipeiLevelTime(grant.endsAt) : t('levelValidityNONE')} · {grant.reason}
          </p>
          {grant.endedEarlyAt ? (
            <p>
              {taipeiLevelTime(grant.endedEarlyAt)} · {grant.endEarlyReason}
            </p>
          ) : (
            (!grant.endsAt || new Date(grant.endsAt) > new Date()) &&
            canUpdate && (
              <button
                type="button"
                className={`${button} mt-2`}
                disabled={mutation.isPending}
                onClick={() => {
                  setEndId(grant.id);
                  setReason('');
                }}
              >
                {t('endLevelGrant')}
              </button>
            )
          )}
        </div>
      ))}
      {showForm && (
        <div id={formId} className="space-y-3 border-t border-base-300 pt-3">
          {endId ? (
            <p className="text-sm font-medium">
              {t('endLevelGrant')} ·{' '}
              {query.data?.grants.find((g) => g.id === endId)?.level.displayName}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <label htmlFor={`${formId}-level`} className={labelClass}>
                  {t('memberTier')}
                </label>
                <select
                  id={`${formId}-level`}
                  value={levelId}
                  disabled={mutation.isPending}
                  onChange={(e) => setLevelId(e.target.value)}
                  className={field}
                >
                  <option value="">{t('selectLevel')}</option>
                  {levels
                    .filter((l) => l.isActive && !l.isDefault)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.displayName}
                      </option>
                    ))}
                </select>
              </div>
              <div className="min-w-0">
                <label htmlFor={`${formId}-validity`} className={labelClass}>
                  {t('levelValidity')}
                </label>
                <select
                  id={`${formId}-validity`}
                  value={validity}
                  disabled={mutation.isPending}
                  onChange={(e) => setValidity(e.target.value as MemberLevelValidity)}
                  className={field}
                >
                  {(['WEEK', 'MONTH', 'NONE'] as const).map((v) => (
                    <option key={v} value={v}>
                      {t(`levelValidity${v}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div>
            <label htmlFor={`${formId}-reason`} className={labelClass}>
              {t('levelGrantReason')}
            </label>
            <input
              id={`${formId}-reason`}
              maxLength={500}
              value={reason}
              disabled={mutation.isPending}
              onChange={(e) => setReason(e.target.value)}
              className={field}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex min-h-11 items-center px-4 rounded-lg bg-primary text-primary-content text-sm font-medium disabled:opacity-50"
              disabled={mutation.isPending || !key.ready || !reason.trim() || (!endId && !levelId)}
              onClick={() => mutation.mutate()}
            >
              {endId ? t('endLevelGrant') : t('grantLevel')}
            </button>
            <button
              type="button"
              className={button}
              disabled={mutation.isPending}
              onClick={closeForm}
            >
              {t('common:cancel')}
            </button>
          </div>
          {mutation.isError && (
            <p role="alert" className="text-error">
              {mutation.error instanceof Error ? mutation.error.message : t('updateFailed')}
            </p>
          )}
        </div>
      )}
      <details>
        <summary className="min-h-11 cursor-pointer font-medium">{t('levelHistory')}</summary>
        {query.data?.history.length === 0 && (
          <p className="text-sm text-base-content/70">{t('levelHistoryEmpty')}</p>
        )}
        {query.data?.history.map((row) => (
          <p key={row.id} className="text-sm tabular-nums">
            {taipeiLevelTime(row.snapshot.happenedAt)} · {name(row.snapshot.old.levelId)} →{' '}
            {name(row.snapshot.next.levelId)} · {row.message} ·{' '}
            {row.actorUsername ?? t('levelSystemActor')}
          </p>
        ))}
      </details>
    </section>
  );
}
